import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { ConfirmationService } from 'primeng/api';
import { GetPersonalProfile } from '../../../libs/actions/personal-profile.action';
import { ApiService } from '../../../libs/services/api-service/api.service';
import { CustomKeycloakService } from '../../../libs/services/custom-keycloak-service/custom-keycloak.service';
import { PersonalProfileState } from '../../../libs/state/personal-profile.state';
import { hasFinxoneAdminRole } from '../../../libs/utils/finxone-admin-role.utils';
import { SHARED_CONSTANTS } from '../shared.constants';

const NAV_COLLAPSED_KEY = 'fnx-nav-collapsed';

@Component({
  standalone: false,
  selector: 'finxone-platform-left-menus',
  templateUrl: './left-menus.component.html',
  styleUrls: ['./left-menus.component.scss'],
})
export class LeftMenusComponent implements OnInit {
  public sharedConstants = SHARED_CONSTANTS;
  public firstName: string;
  public lastName: string;
  public email: string;
  public activeMenu: 'appBuilder' | 'adminPortal' = 'appBuilder';
  public showDashboardDropdown = false;
  public userAdmin: boolean = false;
  public menuLabel: string;
  /** Icon-rail mode — persisted so screens that need the room (Widget Studio) keep it. */
  public collapsed = localStorage.getItem(NAV_COLLAPSED_KEY) === '1';
  constructor(
    private router: Router,
    private apiService: ApiService,
    private confirmationService: ConfirmationService,
    private customKeycloakService: CustomKeycloakService,
    private store: Store,
  ) {
    // Builder tools are emitted as one group. Their order follows the authoring journey:
    // define UI and data, add backend behaviour and its outbound dependencies, then compose
    // everything into a releasable solution.
    this.menus['appBuilder'].splice(1, 0, {
      title: this.sharedConstants.BUILD,
      img: '',
      isChild: true,
      isActive: false,
      url: '',
      imgClass: '',
      children: [
        {
          title: this.sharedConstants.WIDGET_STUDIO,
          img: 'my-widgets',
          isActive: false,
          url: '/back-office/widget-studio',
          imgClass: '',
        },
        {
          title: this.sharedConstants.DATA_STUDIO,
          img: 'template-editor',
          isActive: false,
          url: '/back-office/data-studio',
          imgClass: '',
        },
        {
          title: this.sharedConstants.FUNCTION_STUDIO,
          img: 'template-editor',
          isActive: false,
          url: '/back-office/function-studio',
          imgClass: '',
        },
        {
          title: this.sharedConstants.CONNECTIONS,
          img: 'globe',
          isActive: false,
          url: '/back-office/outbound-connections',
          imgClass: '',
        },
        {
          title: this.sharedConstants.SOLUTIONS,
          img: 'buildings',
          isActive: false,
          url: '/back-office/solutions',
          imgClass: '',
        },
      ],
    });
    this.showActiveMenu(this.router.url, 'appBuilder');
    this.router.events.subscribe((res) => {
      this.activeMenu = this.router.url.includes('/admin-portal/') ? 'adminPortal' : 'appBuilder';
      this.menuLabel = this.dashboardDropdownItems.find((i) => i.menuName === this.activeMenu)?.label || '';
      this.showActiveMenu(this.router.url, this.activeMenu);
    });
    setTimeout(() => {
      this.store.dispatch(new GetPersonalProfile());
      this.getProfileApi();
    }, 3000);
    this.apiService.reloadUser.subscribe((res: boolean) => {
      this.getProfileApi();
    });
    this.isUserAdmin();
  }

  ngOnInit() {
    this.menuLabel = this.dashboardDropdownItems.find((i) => i.menuName === this.activeMenu)?.label || '';
  }
  public logoPath = 'assets/images/finx1-logo.svg';
  public menus: {
    [key: string]: {
      title: string;
      img: string;
      isChild: boolean;
      isActive: boolean;
      imgClass: string;
      url: string;
      children: {
        title: string;
        img: string;
        isActive: boolean;
        imgClass: string;
        url: string;
      }[];
    }[];
  } = {
    appBuilder: [
      {
        title: this.sharedConstants.DASHBOARD,
        img: 'dashboard',
        isChild: false,
        isActive: true,
        url: '/back-office/dashboard',
        imgClass: '',
        children: [],
      },
      {
        title: this.sharedConstants.PERSONAL,
        img: '',
        isChild: true,
        imgClass: '',
        isActive: false,
        url: '',
        children: [
          {
            title: this.sharedConstants.PROFILE,
            img: 'user',
            isActive: false,
            imgClass: '',
            url: '/back-office/profile',
          },
        ],
      },
      {
        title: this.sharedConstants.COMPANY,
        isChild: true,
        isActive: false,
        imgClass: '',
        img: '',
        url: '',
        children: [
          {
            title: this.sharedConstants.DETAILS,
            img: 'buildings',
            isActive: false,
            url: '/back-office/company-profile',
            imgClass: '',
          },
          {
            title: this.sharedConstants.TEAM_MEMBERS,
            img: 'users-icon',
            isActive: false,
            url: '/back-office/team-members',
            imgClass: '',
          },
          {
            title: this.sharedConstants.BILLING,
            img: 'my-widgets',
            isActive: false,
            url: '/back-office/billing',
            imgClass: '',
          },
          {
            title: this.sharedConstants.FIGMA,
            img: 'buildings',
            isActive: false,
            url: '/back-office/figma',
            imgClass: '',
          },
        ],
      },
    ],
    adminPortal: [
      {
        title: this.sharedConstants.DASHBOARD,
        img: 'dashboard',
        isChild: false,
        isActive: true,
        url: '/admin-portal/dashboard',
        imgClass: '',
        children: [],
      },
      {
        title: this.sharedConstants.PROJECTS,
        img: 'buildings',
        isChild: false,
        isActive: false,
        url: '/admin-portal/projects',
        imgClass: '',
        children: [],
      },
      {
        title: this.sharedConstants.ORGANISATIONS,
        img: 'buildings',
        isChild: false,
        isActive: false,
        url: '/admin-portal/organisations',
        imgClass: '',
        children: [],
      },
      {
        title: this.sharedConstants.USERS,
        img: 'users-icon',
        isChild: false,
        isActive: false,
        url: '/admin-portal/users',
        imgClass: '',
        children: [],
      },
      {
        title: this.sharedConstants.AGENT_SPEND,
        img: 'my-widgets',
        isChild: false,
        isActive: false,
        url: '/admin-portal/agent-spend',
        imgClass: '',
        children: [],
      },
      {
        title: 'Bug reports',
        img: 'dashboard',
        isChild: false,
        isActive: false,
        url: '/admin-portal/bug-reports',
        imgClass: '',
        children: [],
      },
      {
        title: this.sharedConstants.AGENT_DASHBOARD,
        img: 'dashboard',
        isChild: false,
        isActive: false,
        url: '/admin-portal/agent-dashboard',
        imgClass: '',
        children: [],
      },
      {
        title: this.sharedConstants.BILLING,
        img: 'my-widgets',
        isChild: false,
        isActive: false,
        url: '/admin-portal/billing',
        imgClass: '',
        children: [],
      },
      {
        title: this.sharedConstants.TEMPLATE_EDITOR,
        img: 'template-editor',
        isChild: false,
        isActive: false,
        url: '/ui-builder/template-editor',
        imgClass: '',
        children: [],
      },
    ],
  };

  public dashboardDropdownItems = [
    { label: 'App builder', menuName: 'appBuilder', url: '/back-office/dashboard' },
    { label: 'Admin portal', menuName: 'adminPortal', url: '/admin-portal/dashboard' },
  ];

  showActiveMenu(url: string, activeMenu: string) {
    for (const menu of this.menus[activeMenu]) {
      if (menu.isChild) {
        for (const child of menu.children) {
          if (child.url == url) {
            child.isActive = true;
          } else {
            child.isActive = false;
          }
        }
      } else {
        if (menu.url == url) {
          menu.isActive = true;
        } else {
          menu.isActive = false;
        }
      }
    }
  }
  onClickMenu(url: string) {
    this.router.navigate([url]);
  }

  private getProfileApi() {
    this.store.select(PersonalProfileState.getPersonalProfileDetail).subscribe((user) => {
      this.firstName = user.firstName ? user.firstName : '';
      this.lastName = user.lastName ? user.lastName : '';
      this.email = user.email ? user.email : '';
    });
  }

  logout() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to <b>log out?</b>',
      accept: () => {
        this.customKeycloakService.logout();
      },
    });
  }

  public toggleDashboardDropdown() {
    this.showDashboardDropdown = !this.showDashboardDropdown;
  }

  public toggleCollapsed() {
    this.collapsed = !this.collapsed;
    localStorage.setItem(NAV_COLLAPSED_KEY, this.collapsed ? '1' : '0');
  }

  public clickDropdownItem(item: { label: string; menuName: string; url: string }) {
    this.activeMenu = item.menuName as 'appBuilder' | 'adminPortal';
    this.menuLabel = this.dashboardDropdownItems.find((i) => i.menuName === item.menuName)?.label || '';
    this.showDashboardDropdown = false;
    this.router.navigate([item.url]);
  }

  private isUserAdmin() {
    this.userAdmin = hasFinxoneAdminRole();
  }
}
